/**
 * AccountsPanel — searchable accounts list with drill-down + impersonate.
 *
 * Plan §3.7: each row has counts (clients / campaigns / runs) and an
 * "Impersonate" affordance that mints a short-lived session bound to
 * the target account. The audit chain records the impersonation.
 */

import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useCallback, useEffect, useState } from "react";

import { ApiError, api, type AccountDetail, type AccountSummary } from "../lib/api";

export function AccountsPanel() {
  const [rows, setRows] = useState<AccountSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<AccountDetail | null>(null);
  const [impersonating, setImpersonating] = useState<string | null>(null);
  const [clearing, setClearing] = useState(false);
  const [clearMessage, setClearMessage] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setError(null);
      const r = await api.accountsList({ q: search.trim() || undefined, limit: 100 });
      setRows(r.accounts);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "fetch failed");
    }
  }, [search]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openDetail = async (id: string) => {
    try {
      setSelected(await api.accountDetail(id));
      setClearMessage(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "fetch failed");
    }
  };

  const clearKeypair = async (id: string, email: string) => {
    const confirmed = window.confirm(
      `Clear keypair for ${email}?\n\n` +
        "This wipes:\n" +
        "  • office_public_key\n" +
        "  • wrapped O_sk\n" +
        "  • published cert\n\n" +
        "The accountant will go through the keypair wizard on next " +
        "login. Files uploaded under the old key become UNREADABLE. " +
        "Use this only when the accountant has lost their passphrase " +
        "or the wrapped blob is wedged.",
    );
    if (!confirmed) return;
    setClearing(true);
    setClearMessage(null);
    try {
      const resp = await api.clearKeypair(id);
      setClearMessage(
        resp.already_clear
          ? "Account had no keypair to clear."
          : "Keypair cleared. Accountant will re-enroll on next login.",
      );
    } catch (err) {
      setClearMessage(
        err instanceof ApiError ? `Error: ${err.detail}` : "clear failed",
      );
    } finally {
      setClearing(false);
    }
  };

  const blockAccount = async (id: string, email: string) => {
    const reason = window.prompt(
      `Block ${email}?\n\n` +
        "This:\n" +
        "  • flips signup_status='blocked'\n" +
        "  • revokes ALL active sessions for the account\n" +
        "  • audits the reason\n" +
        "Login will refuse with signup_blocked. Reversible via " +
        "Unblock — but the user has to log in fresh.\n\n" +
        "Enter a reason (audited, not emailed to the user):",
    );
    if (!reason || !reason.trim()) return;
    try {
      const result = await api.blockAccount(id, reason.trim());
      alert(
        `${result.email} blocked.\n` +
          `Sessions revoked: ${result.sessions_revoked}\n` +
          "Login is now refused for this account.",
      );
      await refresh();
    } catch (err) {
      const detail =
        err instanceof ApiError ? err.detail : "block_account failed";
      if (detail === "cannot_block_operator_account") {
        alert("Refused: operator accounts can't be blocked from this UI.");
      } else {
        setError(detail);
      }
    }
  };

  const unblockAccount = async (id: string, email: string) => {
    if (
      !window.confirm(
        `Unblock ${email}?\n\n` +
          "signup_status goes back to 'approved'. Sessions stay " +
          "revoked — the user has to log in fresh.",
      )
    ) {
      return;
    }
    try {
      await api.unblockAccount(id);
      await refresh();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.detail : "unblock_account failed",
      );
    }
  };

  const resetPassword = async (id: string, email: string) => {
    if (
      !window.confirm(
        `Reset password for ${email}?\n\n` +
          "This invalidates EVERY active session for that account and " +
          "mints a one-time random password. Communicate it to the user " +
          "out-of-band; they should change it on first login.",
      )
    ) {
      return;
    }
    try {
      const result = await api.resetPassword(id);
      await navigator.clipboard
        ?.writeText(result.temporary_password)
        .catch(() => {});
      alert(
        `Password reset for ${result.email}.\n\n` +
          `Temporary password (copied to clipboard):\n${result.temporary_password}\n\n` +
          `Sessions revoked: ${result.sessions_revoked}\n` +
          `This is shown ONCE — close this dialog and the value is gone.`,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "reset_password failed");
    }
  };

  const impersonate = async (id: string) => {
    setImpersonating(id);
    try {
      const result = await api.impersonate(id);
      // Surface the token to the operator. They can paste it into the
      // dashboard's localStorage to "View as" — stage 2 will replace
      // this with a redirect that auto-installs the token.
      const text = `access_token=${result.access_token}\nactor.accountant_id=${result.actor.accountant_id}`;
      await navigator.clipboard?.writeText(result.access_token).catch(() => {});
      alert(
        "Impersonation session minted. Access token copied to clipboard.\n\n" +
          text +
          "\n\nPaste into the dashboard's localStorage under 'dovach.access_token' to view as this account.\n" +
          "All page-views are audited.",
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "impersonate failed");
    } finally {
      setImpersonating(null);
    }
  };

  if (error) return <Alert color="red">{error}</Alert>;

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>Accounts</Title>
        <Group>
          <TextInput
            placeholder="Search email or name…"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") refresh();
            }}
            w={280}
          />
          <Button onClick={refresh} variant="light">
            Search
          </Button>
        </Group>
      </Group>

      {rows === null ? (
        <Loader size="sm" />
      ) : rows.length === 0 ? (
        <Paper p="xl" withBorder>
          <Text c="dimmed">No matching accounts.</Text>
        </Paper>
      ) : (
        <Paper withBorder>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Email</Table.Th>
                <Table.Th>Display name</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Tier</Table.Th>
                <Table.Th>2FA</Table.Th>
                <Table.Th>Operator</Table.Th>
                <Table.Th>Created</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((a) => (
                <Table.Tr key={a.account_id}>
                  <Table.Td>{a.email}</Table.Td>
                  <Table.Td dir="rtl">{a.display_name}</Table.Td>
                  <Table.Td>{renderStatusBadge(a.signup_status)}</Table.Td>
                  <Table.Td>{a.subscription_tier}</Table.Td>
                  <Table.Td>
                    {a.has_2fa ? (
                      <Badge color="green" variant="light">on</Badge>
                    ) : (
                      <Badge color="gray" variant="light">off</Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {a.is_operator && <Badge color="violet">operator</Badge>}
                  </Table.Td>
                  <Table.Td>{new Date(a.created_at).toLocaleDateString()}</Table.Td>
                  <Table.Td>
                    <Group gap={6}>
                      <Button size="xs" variant="light" onClick={() => openDetail(a.account_id)}>
                        Detail
                      </Button>
                      <Button
                        size="xs"
                        color="violet"
                        variant="light"
                        loading={impersonating === a.account_id}
                        onClick={() => impersonate(a.account_id)}
                      >
                        View as
                      </Button>
                      <Button
                        size="xs"
                        color="orange"
                        variant="light"
                        onClick={() => resetPassword(a.account_id, a.email)}
                      >
                        Reset pw
                      </Button>
                      {a.signup_status === "blocked" ? (
                        <Button
                          size="xs"
                          color="green"
                          variant="light"
                          onClick={() => unblockAccount(a.account_id, a.email)}
                        >
                          Unblock
                        </Button>
                      ) : a.signup_status === "approved" && !a.is_operator ? (
                        <Button
                          size="xs"
                          color="red"
                          variant="light"
                          onClick={() => blockAccount(a.account_id, a.email)}
                        >
                          Block
                        </Button>
                      ) : null}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      <Modal
        opened={selected !== null}
        onClose={() => setSelected(null)}
        title={selected ? `Account · ${selected.email}` : "Account"}
        size="md"
      >
        {selected && (
          <Stack>
            <Group>
              <Stat label="Display name" value={selected.display_name} dir="rtl" />
              <Stat label="Phone" value={selected.phone_e164 ?? "—"} />
            </Group>
            <Group>
              <Stat label="Tier" value={selected.subscription_tier} />
              <Stat label="2FA" value={selected.has_2fa ? "enabled" : "disabled"} />
              <Stat label="Operator" value={selected.is_operator ? "yes" : "no"} />
            </Group>
            <Group>
              <Stat label="Clients" value={String(selected.counts.clients)} />
              <Stat label="Campaigns" value={String(selected.counts.campaigns)} />
              <Stat label="Runs" value={String(selected.counts.runs)} />
            </Group>
            <Stat label="Created" value={new Date(selected.created_at).toLocaleString()} />
            {selected.deleted_at && (
              <Alert color="red">
                This account is soft-deleted ({new Date(selected.deleted_at).toLocaleString()}).
              </Alert>
            )}

            <Paper p="sm" withBorder style={{ borderColor: "#fa5252" }}>
              <Stack gap="xs">
                <Text fw={500} c="red">
                  Danger zone
                </Text>
                <Text size="xs" c="dimmed">
                  Force a re-enrollment by wiping the accountant's keypair
                  + cert. Files uploaded under the old key become
                  unreadable. Audited as the operator.
                </Text>
                {clearMessage && (
                  <Alert
                    color={clearMessage.startsWith("Error") ? "red" : "green"}
                  >
                    {clearMessage}
                  </Alert>
                )}
                <Button
                  size="xs"
                  color="red"
                  variant="light"
                  loading={clearing}
                  onClick={() => clearKeypair(selected.account_id, selected.email)}
                >
                  Clear keypair (force re-enrollment)
                </Button>
              </Stack>
            </Paper>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}

function renderStatusBadge(status: string) {
  switch (status) {
    case "approved":
      return <Badge color="green" variant="light">approved</Badge>;
    case "pending_review":
      return <Badge color="yellow" variant="light">pending</Badge>;
    case "rejected":
      return <Badge color="red" variant="light">rejected</Badge>;
    case "blocked":
      return <Badge color="red" variant="filled">blocked</Badge>;
    default:
      // Future signup_status values (or anything stale) shouldn't blow
      // up the table — render the raw string so the operator at least
      // sees something.
      return <Badge color="gray" variant="light">{status}</Badge>;
  }
}

function Stat({ label, value, dir }: { label: string; value: string; dir?: "rtl" | "ltr" }) {
  return (
    <div>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text fw={500} dir={dir ?? "ltr"}>
        {value}
      </Text>
    </div>
  );
}
